// ═══════════════════════════════════════════════════════════════════
//  THE DAY GRID
//
//  One team's day: a column per team member, a row per bookable slot,
//  with real Cal bookings laid over it. Pure — no fetching, no clock, no
//  browser time zone. Every time is resolved against the team's zone.
//
//  ROW HEIGHT IS THE TEAM'S OWN SLOT LENGTH, read from its Cal event type
//  (`lengthInMinutes`). A team books 30-minute intakes or 15-minute ones;
//  because the grid shows one team at a time there is no need to find a
//  common divisor the way a merged multi-site week would.
// ═══════════════════════════════════════════════════════════════════

import type { AvailabilityOverride, BookingDetails, GridWindow, HostAvailability } from "../bookingCalendarApi"
import { isOutsideBookingSeason } from "./bookingSeason"
import type { BookingSeasonBounds } from "./bookingSeason"
import type { CalendarDate, MinuteOfDay } from "./siteTime"
import { minutesToLabel, zonedDateAndMinutes, zonedTimeToInstant } from "./siteTime"
import { slotTypeEventTypes, slotTypeForEventType, slotTypeForWindows } from "./slotTypes"
import type { SlotType, SlotTypeEventTypes } from "./slotTypes"

export type { SlotType } from "./slotTypes"

// Every row the grid draws is inside business hours, so a row is free, taken, or one its host is
// not bookable for. There is no "closed" row to render any more.
export type SlotState = "booked" | "open" | "unavailable"

/**
 * Why a row cannot be booked: nobody is working it.
 *
 * On a free row this covers every reason Cal would not sell the hour — the site's own hours, its
 * standing breaks, a schedule that never reached the row, somebody's absence.
 *
 * Carried on booked rows too, but there it means one thing only: a date override says the host is not
 * there, and an appointment is standing in it anyway. That is the case a manager most needs to see —
 * a booking taken before the absence was entered, and one somebody now has to move — and hiding it
 * behind the appointment would bury exactly that. The reasons an hour is merely unsellable are not
 * repeated on a booking that was already taken in it; see `markUnavailable`.
 */
export interface UnavailabilityMark {
  /** What to show: the reason somebody wrote, or "Site closed" / "Unavailable" when nobody did. */
  label: string
  /** The free text as recorded, absent when nobody gave one. */
  reason?: string
  /** Whose absence it is — a whole site (`event-type`) or this one person (`host`). */
  scope?: AvailabilityOverride["scope"]
  /** Part of this row IS bookable, so the row is not wholly dead time. */
  partial?: boolean
  /**
   * The office's own break or lunch, not anybody's absence. Drawn quietly and never called out.
   *
   * Only ever on a free row: a break is a reason an hour is unsellable, which says nothing about an
   * appointment already booked in it.
   */
  scheduledBreak?: boolean
  /**
   * The host has already reached their daily limit of appointments that day (AB#606). Cal applies
   * the cap by withholding the rest of the day, so it arrives exactly like a day off; the server's
   * own count (`HostAvailability.cappedDays`) is what tells the two apart. Only ever on a free row:
   * the appointments that make up the limit are not a conflict.
   */
  dailyLimit?: boolean
}

export interface SlotData {
  /** Team-local label for this row, e.g. "9:15 AM". */
  time: string
  /** Team-local minutes past midnight — the row's identity, independent of its label. */
  startMinutes: MinuteOfDay
  state: SlotState
  /** First attendee's name, for the compact cell. */
  client?: string
  /**
   * Which kind of appointment the row is (AB#779) — public, private or managed — from the Cal event
   * type behind it: a booked row's own booking, or the windows that make a free row bookable.
   *
   * Set on every booked row, and on a free row only when availability was read; unset on a free row
   * means public (`slotTypeOf`). Where on the grid an appointment sits is not part of it: that is
   * `offGrid` and `outsideBusinessHours`.
   */
  slotType?: SlotType
  /** The booking's Cal uid, on both the head row and the rows it spans. */
  slotId?: string
  booking?: BookingDetails
  /** The MSC office this appointment is at. */
  siteName?: string
  /** Rows this appointment occupies, on its head row. A 60-minute booking on 30-minute rows → 2. */
  spanRows?: number
  /** Set on the rows a spanning appointment covers, so they render as one block rather than several. */
  continuation?: boolean
  /** The appointment's own start time, which is not the row's when it is off the cadence. */
  bookedAt?: string
  /** The appointment did not start on a row boundary (e.g. 9:07); it is drawn on the row containing it. */
  offGrid?: boolean
  /** The appointment falls outside the day's business hours. */
  outsideBusinessHours?: boolean
  /** Another appointment covers this row too. Both are kept; neither is silently dropped. */
  hasOverlap?: boolean
  /**
   * The other appointments this one collides with, on its head row: ones that could not be given a
   * row of their own here, and ones whose span was cut short against it. Empty unless `hasOverlap`.
   */
  overlapping?: BookingDetails[]
  /** Why this row cannot be booked, when it cannot. */
  unavailable?: UnavailabilityMark
  /**
   * The public could not book a slot starting here: it falls outside the booking season, or no
   * season is set. Staff still can, so this changes nothing about `state` — an open row stays open
   * and clickable. Set on every row it is true of, whatever its state; the cell decides what to draw.
   */
  outsideBookingSeason?: boolean
}

/** One day's cells, keyed by grid column (a team member). Every column has the same row count. */
export interface DaySchedule {
  [columnId: string]: SlotData[]
}

/** How far the day in view has got: a real fetch's state, not a placeholder. */
export type DayLoadStatus = "idle" | "loading" | "succeeded" | "failed"

/** A grid column: one team member, identified in Cal terms because that is what a booking's hosts carry. */
export interface GridColumn {
  id: string
  calUserId: number
}

/** The window the grid draws, as minutes past midnight. */
export interface BusinessHours {
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
}

/**
 * The window a main office's grid draws: 8:30 AM to 4:30 PM, the hours the main offices take
 * appointments in. The grid draws these hours and nothing else — with 30-minute slots the first row
 * is 8:30 AM and the last is 4:00 PM, ending at close.
 *
 * It opens half an hour before intake does and closes an hour after because off-slot does:
 * `SiteEventTypeDefaults` provisions 8:30–9:00 and 4:00–4:30 off-slot windows on every weekday, and
 * a row the office can book into is a row the grid has to have. On an ordinary intake day those rows
 * draw as `slotType: "managed"`, which is exactly what they are. An outreach site draws in
 * `OUTREACH_BUSINESS_HOURS` instead — see `businessHoursFor`.
 *
 * A UI-side default, deliberately: Cal holds the real per-team availability on the event type's
 * schedule, and CMS exposes no endpoint that reads it. So this is the frame, not the truth — see
 * `bookingsOutsideWindow` for the appointments it cannot show.
 *
 * Only the fallback now: the server sends each site's window from configuration (`gridWindow`,
 * AB#783, defaulting to these same hours), and this stands in when a response carries none.
 */
export const DEFAULT_BUSINESS_HOURS: BusinessHours = { startMinutes: (8 * 60) + 30, endMinutes: (16 * 60) + 30 }

/**
 * The window an outreach site's grid draws: 8:30 AM to noon. An outreach site takes intake in the
 * morning only, Friday included, and `SiteEventTypeDefaults` gives its off-slot just the 8:30 half
 * hour every weekday, with no afternoon and no 4:00–4:30 — so nothing after noon is bookable there
 * and the grid does not draw it. The fallback for an outreach site's `gridWindow`, like
 * `DEFAULT_BUSINESS_HOURS`.
 */
export const OUTREACH_BUSINESS_HOURS: BusinessHours = { startMinutes: (8 * 60) + 30, endMinutes: 12 * 60 }

/** A site in view as the server describes it: its Cal team slug, and whether it is a main office. */
export interface SiteKind {
  /** Absent for a team Cal never slugged. */
  slug?: string | null
  isMainOffice: boolean
  /**
   * The window the server configures for this kind of site (AB#783), `end` exclusive. Absent from an
   * older API, when the constants above stand in for it.
   */
  gridWindow?: GridWindow | null
}

/**
 * One site's window: the server's, when it sent one that reads as a window, otherwise the constant
 * for its kind of site.
 */
function siteBusinessHours(site: SiteKind): BusinessHours {
  const startMinutes = wallClockMinutes(site.gridWindow?.start)
  const endMinutes = wallClockMinutes(site.gridWindow?.end)
  if (startMinutes != null && endMinutes != null && endMinutes > startMinutes) {
    return { startMinutes, endMinutes }
  }
  return site.isMainOffice ? DEFAULT_BUSINESS_HOURS : OUTREACH_BUSINESS_HOURS
}

/**
 * The window to draw for the sites in view: the outreach morning when every one of them is an
 * outreach site, otherwise the main office's whole day — a week that spans both kinds of site needs
 * the rows of the longer one. Each site's window is the one the server sends (`gridWindow`, from
 * configuration), and the constants above only when it sends none; across sites the grid draws from
 * the earliest start to the latest end.
 *
 * Which kind a site is comes from the server (`ServiceAreaService.IsMainOffice`), never a list kept
 * here. A site with no slug — the team has not loaded, or Cal never slugged it — is left out, so
 * nothing at all known draws the whole day: nobody has yet said this is a site that closes at noon.
 */
export function businessHoursFor(sites: readonly (SiteKind | null | undefined)[]): BusinessHours {
  const known = sites.filter((site): site is SiteKind => !!site?.slug)
  if (known.length === 0) return DEFAULT_BUSINESS_HOURS

  const windows = known.map(siteBusinessHours)
  return {
    startMinutes: Math.min(...windows.map(w => w.startMinutes)),
    endMinutes: Math.max(...windows.map(w => w.endMinutes)),
  }
}

/** One of the office's own breaks: everybody's, every day, and nobody's absence. */
interface ScheduledBreak {
  label: string
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
}

/**
 * The standing breaks in an office day, in the team's own clock.
 *
 * Cal only ever says when somebody IS bookable, so the mid-morning break, lunch and the afternoon
 * break arrive here as gaps between a day's windows — `SiteEventTypeDefaults` provisions 9:00–10:00,
 * 10:30–12:00, 13:00–15:30 and, for off-slot, 16:00–16:30 — and are otherwise indistinguishable from
 * time somebody has taken off. Naming them is what keeps the grid from reporting the whole team as
 * unavailable at lunch.
 *
 * A UI-side mirror of the provisioned hours, like `DEFAULT_BUSINESS_HOURS`: CMS exposes no endpoint
 * that reads a schedule's windows back, so this is the frame rather than the truth. Getting it wrong
 * costs a break drawn as an ordinary absence, never a slot wrongly offered — the rows come from
 * Cal's own availability either way.
 */
export const SCHEDULED_BREAKS: readonly ScheduledBreak[] = [
  { label: "Break", startMinutes: 10 * 60, endMinutes: (10 * 60) + 30 },
  { label: "Lunch", startMinutes: 12 * 60, endMinutes: 13 * 60 },
  { label: "Break", startMinutes: (15 * 60) + 30, endMinutes: 16 * 60 },
]

/**
 * The standing break a row falls wholly inside, when it does.
 *
 * Wholly, because a row that only overlaps a break is a row with bookable time in it, and its dead
 * half is not the break's doing.
 */
function breakAt(rowStart: MinuteOfDay, slotMinutes: number): ScheduledBreak | undefined {
  return SCHEDULED_BREAKS.find(b => b.startMinutes <= rowStart && b.endMinutes >= rowStart + slotMinutes)
}

/** Whether the gap between two dead rows is nothing but a standing break. */
function onlyBreakBetween(from: MinuteOfDay, to: MinuteOfDay): boolean {
  return from === to || SCHEDULED_BREAKS.some(b => b.startMinutes <= from && b.endMinutes >= to)
}

export interface BuildDayGridInput {
  /** The day being drawn, as a team-local calendar date. */
  date: CalendarDate
  bookings: BookingDetails[]
  columns: GridColumn[]
  /** Row height, and the length of an ordinary appointment here. From the team's Cal event type. */
  slotMinutes: number
  /** IANA zone the booking instants are resolved against. */
  timeZone: string
  /**
   * When each host is bookable, from Cal.
   *
   * Omitted means "not known" rather than "nobody is available" — the grid then draws ordinary open
   * days, which is what it did before availability could be read at all. A host absent from a list
   * that IS given is a host with no bookable time, which is a different statement and is drawn as
   * one.
   */
  availability?: HostAvailability[]
  /**
   * The site's off-slot (Managed) event types, by Cal id — what a date range's or a booking's
   * `eventTypeId` is matched against to give its row `slotType: "managed"`.
   *
   * Omitted or empty, with `privateEventTypeIds` too, makes every row public, which is what a caller
   * that cannot tell the event types apart should get: the rows are still drawn as free, because
   * they are.
   */
  offSlotEventTypeIds?: readonly number[]
  /** The site's private event types, by Cal id — matched the same way, for `slotType: "private"`. */
  privateEventTypeIds?: readonly number[]
  businessHours?: BusinessHours
  /**
   * When the public may book, to mark the rows they may not — see `outsideBookingSeason`.
   *
   * Omitted means "not known", and marks nothing: a calendar that cannot read the season says
   * nothing about it rather than flagging every row.
   */
  bookingSeason?: BookingSeasonBounds
}

// Cal's status vocabulary mixes state with time: accepted / pending / awaiting_host are live,
// cancelled and rejected are not. A cancelled appointment must never occupy a slot, or the day
// shows as full when it is not.
const CANCELLED_STATUSES = new Set(["cancelled", "canceled", "rejected"])

export function isLiveBooking(booking: BookingDetails): boolean {
  return !CANCELLED_STATUSES.has((booking.status ?? "").trim().toLowerCase())
}

/**
 * The Cal ids of every host with another live appointment overlapping `booking`'s time — the hosts
 * it cannot be moved to. The server refuses that move, override or not, because Cal will not hold two
 * appointments for one person at once; this is what lets the move dialog say so before anyone asks.
 *
 * Only as complete as `bookings`, which is one team's day: an appointment the host has at another
 * site is not in it. The server's own check is the one that decides.
 */
export function hostsBookedDuring(booking: BookingDetails, bookings: readonly BookingDetails[]): Set<number> {
  const start = Date.parse(booking.start)
  const end = booking.end ? Date.parse(booking.end) : start
  const booked = new Set<number>()

  for (const other of bookings) {
    if (other.uid === booking.uid || !isLiveBooking(other)) continue

    const otherStart = Date.parse(other.start)
    const otherEnd = other.end ? Date.parse(other.end) : otherStart

    // The same start always counts, so an appointment with no end still collides with its own slot.
    const overlaps = otherStart === start || (otherStart < end && start < otherEnd)
    if (overlaps) for (const host of other.hosts) booked.add(host.id)
  }

  return booked
}

interface PlacedBooking {
  booking: BookingDetails
  /** Exact team-local start, which is not necessarily on a row boundary. */
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
}

/**
 * Resolves each booking onto the team's wall clock and drops the ones that cannot appear on this
 * day: cancelled or rejected, or belonging to another date (a wider fetch window shares a cache
 * with the visible day, so this is load-bearing, not defensive).
 */
function placeBookings(
  bookings: BookingDetails[],
  date: CalendarDate,
  slotMinutes: number,
  timeZone: string,
): PlacedBooking[] {
  const placed: PlacedBooking[] = []

  for (const booking of bookings) {
    if (!isLiveBooking(booking)) continue

    const start = zonedDateAndMinutes(booking.start, timeZone)
    if (start.date !== date) continue

    // An appointment running past midnight is clamped to the end of its own day rather than
    // wrapping into the next column. A silent wrap would be invisible if one ever occurred.
    const end = booking.end ? zonedDateAndMinutes(booking.end, timeZone) : null
    const rawEnd = end && end.date === date ? end.minutes : end ? 1440 : 0
    const endMinutes = rawEnd > start.minutes ? rawEnd : start.minutes + slotMinutes

    placed.push({ booking, startMinutes: start.minutes, endMinutes })
  }

  // Deterministic order so overlap resolution does not depend on what the API happened to return
  // first: earliest start wins the row, ties broken by uid.
  return placed.sort((a, b) =>
    a.startMinutes - b.startMinutes || a.booking.uid.localeCompare(b.booking.uid))
}

/**
 * The vertical extent of the grid: business hours exactly, one row per slot, and only rows that
 * finish by closing time. On an 8:30–4:30 day at 30-minute slots that is 8:30 AM through 4:00 PM —
 * the last row is the last appointment the office can actually see through to the end.
 */
export function rowAxis(
  slotMinutes: number,
  businessHours: BusinessHours,
): MinuteOfDay[] {
  const rows: MinuteOfDay[] = []
  for (
    let minutes = businessHours.startMinutes;
    minutes + slotMinutes <= businessHours.endMinutes;
    minutes += slotMinutes
  ) {
    rows.push(minutes)
  }
  return rows
}

/** The day's rows before any appointment is laid on top: what the office offers. */
function baseDay(rows: MinuteOfDay[]): SlotData[] {
  return rows.map(startMinutes => ({
    time: minutesToLabel(startMinutes),
    startMinutes,
    state: "open" as SlotState,
  }))
}

/** Lays one appointment onto a column's cells, keeping anything it collides with visible. */
function overlay(
  cells: SlotData[],
  rows: MinuteOfDay[],
  placed: PlacedBooking,
  slotMinutes: number,
  businessHours: BusinessHours,
  slotTypes: SlotTypeEventTypes,
): void {
  const { booking, startMinutes, endMinutes } = placed
  const axisStart = rows[0]!

  // Over before the day starts, or beginning after the last row: there is no row for it. It is not
  // lost — `bookingsOutsideWindow` is how the page still accounts for it.
  if (endMinutes <= axisStart) return

  // Rows are measured from the top of the axis, not from midnight, because the axis is now the
  // office's hours rather than a whole-day grid that happens to contain them.
  const headRow = Math.max(0, Math.floor((startMinutes - axisStart) / slotMinutes))
  if (headRow >= cells.length) return

  const alignedStart = axisStart + (headRow * slotMinutes)
  const span = Math.min(
    Math.max(1, Math.ceil((endMinutes - alignedStart) / slotMinutes)),
    cells.length - headRow)

  // Flagged, not corrected: a 9:07 start is drawn on the 9:00 row and marked, because moving it
  // silently would misreport when the client is actually expected. An appointment that began before
  // the office opened is the same case — pinned to the first row, showing its own start time, so the
  // host does not read as free at 8:30.
  const offGrid = (startMinutes - axisStart) % slotMinutes !== 0
  const outsideBusinessHours =
    startMinutes < businessHours.startMinutes || startMinutes >= businessHours.endMinutes

  // Booked as one kind, which says nothing about where on the grid it sits: a managed appointment
  // made into an ordinary row starts on the cadence and inside hours, and is still one.
  const slotType = slotTypeForEventType(booking.eventTypeId, slotTypes)

  const head = cells[headRow]!
  let displaced: BookingDetails | undefined
  if (head.state === "booked" && head.slotId !== booking.uid) {
    // Two appointments want the same row. The earlier one keeps it and the later is recorded on it
    // rather than overwriting it — a dropped appointment is invisible, and invisible is the one
    // outcome worse than an ugly one. A row that is only the *tail* of an earlier appointment is
    // different: this one starts here, so it takes the row and the earlier span is cut short.
    if (head.continuation) {
      displaced = head.booking
      truncateSpanBefore(cells, headRow, booking)
    } else {
      recordOverlap(head, booking)
      return
    }
  }

  const shared: Partial<SlotData> = {
    state: "booked",
    booking,
    slotId: booking.uid,
    bookedAt: minutesToLabel(startMinutes),
    slotType,
    siteName: booking.siteName ?? booking.location,
    offGrid: offGrid || undefined,
    outsideBusinessHours: outsideBusinessHours || undefined,
  }

  cells[headRow] = {
    ...cells[headRow]!,
    ...shared,
    client: booking.attendees[0]?.name,
    spanRows: span,
    continuation: undefined,
    hasOverlap: undefined,
    overlapping: undefined,
  }
  if (displaced) recordOverlap(cells[headRow]!, displaced)

  for (let offset = 1; offset < span; offset++) {
    const row = headRow + offset
    const existing = cells[row]!
    if (existing.state === "booked" && existing.slotId !== booking.uid) {
      // Ran into the next appointment: stop here and say so on both.
      cells[headRow]!.spanRows = offset
      recordOverlap(cells[headRow]!, existing.booking)
      recordOverlap(headOf(cells, row), booking)
      return
    }
    cells[row] = { ...existing, ...shared, continuation: true }
  }
}

/** Flags a head row as colliding with `other`, keeping each appointment it collides with once. */
function recordOverlap(head: SlotData, other: BookingDetails | undefined): void {
  head.hasOverlap = true
  if (!other || other.uid === head.slotId) return
  if (head.overlapping?.some(o => o.uid === other.uid)) return
  head.overlapping = [...(head.overlapping ?? []), other]
}

/** The head row of the appointment drawn at `row`, which is `row` itself unless it is a continuation. */
function headOf(cells: SlotData[], row: number): SlotData {
  const uid = cells[row]!.slotId
  let candidate = row
  while (candidate > 0 && cells[candidate]!.continuation && cells[candidate - 1]!.slotId === uid) candidate--
  return cells[candidate]!
}

/**
 * Every appointment competing for the head row a cell belongs to: the one drawn there, then the
 * ones it collides with. What the overlap list offers, in the order the grid resolved them.
 */
export function overlapGroup(cell: SlotData): BookingDetails[] {
  return [...(cell.booking ? [cell.booking] : []), ...(cell.overlapping ?? [])]
}

/**
 * The head row of the appointment `uid` in one column, or undefined when it has none there.
 *
 * A continuation row is not an answer: the head is where the collisions are recorded.
 */
export function headCell(cells: readonly SlotData[] | undefined, uid: string): SlotData | undefined {
  return cells?.find(c => c.slotId === uid && !c.continuation)
}

/**
 * Finds an appointment anywhere in one column, including one that lost its row to another and is
 * only recorded on it. Without the second half an appointment reached from the overlap list could
 * not be opened, which is the whole reason the list exists.
 */
export function findBookingInColumn(cells: readonly SlotData[] | undefined, uid: string): BookingDetails | undefined {
  if (!cells) return undefined
  const drawn = headCell(cells, uid)?.booking
  if (drawn) return drawn
  for (const cell of cells) {
    const hidden = cell.overlapping?.find(o => o.uid === uid)
    if (hidden) return hidden
  }
  return undefined
}

/** Any stretch of the day, in team-local minutes. */
interface TimeSpan {
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
}

/** One stretch of a host's bookable time, in team-local minutes, clamped to the day being drawn. */
interface AvailableWindow extends TimeSpan {
  /** Which of the site's event types published it — what gives a free row its slot type. */
  eventTypeId: number
}

/**
 * Resolves a host's available ranges onto the team's wall clock and keeps the parts on this day.
 *
 * Clamped rather than dropped: a range can begin the previous evening or run into the next morning,
 * and the part that lands on the day in view is still the part that matters.
 */
function placeAvailability(
  availability: HostAvailability | undefined,
  date: CalendarDate,
  timeZone: string,
): AvailableWindow[] {
  const windows: AvailableWindow[] = []

  for (const range of availability?.dateRanges ?? []) {
    const start = zonedDateAndMinutes(range.start, timeZone)
    const end = zonedDateAndMinutes(range.end, timeZone)

    // Calendar dates are "YYYY-MM-DD", so lexical order is chronological order.
    if (start.date > date || end.date < date) continue

    const startMinutes = start.date === date ? start.minutes : 0
    const endMinutes = end.date === date ? end.minutes : 1440
    if (endMinutes <= startMinutes) continue

    windows.push({ startMinutes, endMinutes, eventTypeId: range.eventTypeId })
  }

  return windows.sort((a, b) => a.startMinutes - b.startMinutes)
}

/** Whether one of this host's own appointments covers a row — the reason Cal withheld it. */
function bookedOver(booked: PlacedBooking[], span: TimeSpan): boolean {
  return booked.some(p => p.startMinutes < span.endMinutes && p.endMinutes > span.startMinutes)
}

/** "09:00" as minutes past midnight, or null for anything that cannot be read as a time. */
export function wallClockMinutes(time: string | undefined): MinuteOfDay | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? "")
  return match ? (Number(match[1]) * 60) + Number(match[2]) : null
}

/**
 * Whether one schedule's overrides for a day are the reason a stretch of it is dead.
 *
 * An override does not record when somebody is away — it records when they are *there*, replacing
 * the day's ordinary hours outright. So the time it names is the time it leaves bookable, and it
 * explains only what falls outside that. A stretch inside the window is dead for some other reason
 * — the site's own hours, a midday break, a host taken off the rotation — and putting "Dentist
 * appointment" over it would name the wrong cause.
 *
 * Judged against all of the day's windows together, never one at a time: a part-day absence reaches
 * Cal as several windows on one date — out 2:00–3:00 is 6:00–2:00 and 3:00–10:00 — and a 9:00 row is
 * outside the second of those while being squarely inside the first. Taken singly, every window
 * blames every hour the others keep, which flagged ordinary bookings and put the reason over lunch.
 *
 * Cal writes a cancelled day as a zero-length window, which holds no stretch and so explains all of
 * them. So does an override whose times cannot be read, which is the safe way round: a reason shown a
 * little too widely beats a row shaded with none at all.
 */
function explains(
  overrides: readonly AvailabilityOverride[],
  span: { startMinutes: MinuteOfDay; endMinutes: MinuteOfDay },
): boolean {
  return !overrides.some(override => {
    const start = wallClockMinutes(override.startTime)
    const end = wallClockMinutes(override.endTime)
    return start !== null && end !== null && start <= span.startMinutes && span.endMinutes <= end
  })
}

/**
 * Which override to blame one stretch of dead time on.
 *
 * A site closure wins over a personal absence when both explain it: it is the broader truth, and
 * "Snow closure" explains an empty column better than one person's reason does.
 *
 * The override's times are wall clock on the schedule that holds it, which this read does not name.
 * They are compared against the team's clock — the same one, wherever a host's schedule is kept in
 * the zone of the site they work at.
 *
 * The scope stands in for the schedule, since that is what the windows of one day have to be read
 * together against: a host's own overrides are one schedule's. The site's can come from each of its
 * event types, and pooling those errs towards blaming less — an hour either of them still sells is
 * not the closure's doing.
 *
 * A stretch inside one of the office's standing breaks is judged by the time either side of the
 * break instead. A part-day override keeps the rest of the day's ordinary hours, breaks and all — a
 * site out 2:00–5:00 is 9:00–10:00, 10:30–12:00 and 1:00–2:00 — so lunch is outside its windows
 * whether or not the absence has anything to do with it. It is the override's only when the absence
 * reaches it, taking the time on one side or the other — out from noon, lunch is part of the time
 * they are gone. With the office working on both sides it is still just lunch.
 */
function explainingOverride(
  availability: HostAvailability | undefined,
  date: CalendarDate,
  span: { startMinutes: MinuteOfDay; endMinutes: MinuteOfDay },
): AvailabilityOverride | undefined {
  const onDate = (availability?.overrides ?? []).filter(o => o.date === date)
  const standing = SCHEDULED_BREAKS.find(b => b.startMinutes <= span.startMinutes && b.endMinutes >= span.endMinutes)

  // The minute either side of the break: whether a window holds it is whether the override left it.
  const judged = standing
    ? [
      { startMinutes: standing.startMinutes - 1, endMinutes: standing.startMinutes },
      { startMinutes: standing.endMinutes, endMinutes: standing.endMinutes + 1 },
    ]
    : [span]

  for (const scope of ["event-type", "host"] as const) {
    const schedule = onDate.filter(o => o.scope === scope)
    if (schedule.length > 0 && judged.some(s => explains(schedule, s))) {
      return schedule.find(o => o.reason) ?? schedule[0]
    }
  }

  return undefined
}

/** What the grid calls a day a host has filled to their daily limit (AB#606). */
export const DAILY_LIMIT_LABEL = "Daily limit reached"

/**
 * Whether the server counted this host at their daily limit on `date` (AB#606). Cal shows a capped
 * day only as one with no availability, so this is the one thing that says why.
 */
export function isDailyLimitDay(availability: HostAvailability | undefined, date: CalendarDate): boolean {
  return (availability?.cappedDays ?? []).includes(date)
}

/**
 * What to call it when nobody wrote a reason. The scope is still worth saying. An absence somebody
 * entered explains the row before the daily limit does: it is true whatever the day's count.
 */
function unavailableLabel(override: AvailabilityOverride | undefined, dailyLimit = false): string {
  if (override?.reason) return override.reason
  if (override) return override.scope === "event-type" ? "Site closed" : "Unavailable"
  return dailyLimit ? DAILY_LIMIT_LABEL : "Unavailable"
}

/**
 * Takes back the rows a host is not bookable for.
 *
 * Driven by where they ARE available, because that is the only thing Cal actually promises - a row
 * is unavailable precisely when Cal would not sell it. Deriving it the other way round, from the
 * overrides alone, would miss every reason that is not an override: the site's ordinary hours, a
 * schedule that never covered that row, a host taken off the rotation.
 *
 * Runs after the bookings are laid down, and treats the two cases differently on purpose: a free
 * row becomes unavailable, while a booked row stays booked and is merely flagged. An appointment in
 * a window nobody is working is a conflict the office has to resolve, and it can only resolve what
 * it can see.
 *
 * The rows it leaves alone are the bookable ones, and it says one more thing about those: which of
 * the site's event types would take them — see `slotType`.
 */
function markUnavailable(
  cells: SlotData[],
  rows: MinuteOfDay[],
  windows: AvailableWindow[],
  availability: HostAvailability | undefined,
  date: CalendarDate,
  slotMinutes: number,
  /** The site's private and managed event types. Empty makes every free row public. */
  slotTypes: SlotTypeEventTypes,
): void {
  // Cal drops the rest of a capped host's day, so every row it took is the cap's (AB#606).
  const capped = isDailyLimitDay(availability, date)

  rows.forEach((rowStart, index) => {
    const rowEnd = rowStart + slotMinutes
    const span = { startMinutes: rowStart, endMinutes: rowEnd }

    // A slot is bookable only if the whole of it sits inside one window - Cal will not sell half an
    // appointment, and a row that is half covered is not an appointment anyone can keep.
    const covering = windows.filter(w => w.startMinutes <= rowStart && w.endMinutes >= rowEnd)

    if (covering.length > 0) {
      // The row stands. What is left to say about it is what it is bookable *as*: with the site's
      // week split across its event types, a free Friday row takes a managed appointment and no
      // public one, and somebody reading the grid for a slot to offer has to be able to tell — and
      // who may book it turns on it (AB#779). From every window covering it, not any one: a row the
      // public type reaches is a public row whatever else reaches it.
      if (cells[index]!.state === "open") {
        cells[index] = { ...cells[index]!, slotType: slotTypeForWindows(covering.map(w => w.eventTypeId), slotTypes) }
      }

      return
    }

    const partly = windows.some(w => w.startMinutes < rowEnd && w.endMinutes > rowStart)

    // Resolved per row rather than once for the day: a day can hold an absence and the site's own
    // midday break, and each row belongs to whichever of them actually took it.
    const override = explainingOverride(availability, date, span)

    // A reason somebody entered outranks the office's standing break: a day cancelled for snow is a
    // closed day at 12:30 as much as at 11:00, and labelling that row "Lunch" would say the office
    // is otherwise open.
    // The daily limit outranks it the same way: the host can take nobody else that day, lunch or not.
    const standing = override || capped ? undefined : breakAt(rowStart, slotMinutes)

    const cell = cells[index]!

    // A booked row asks a different question from a free one, and only an override answers it.
    //
    // For a free row the question is "can this be sold?", and every reason Cal would not sell it is a
    // reason worth drawing: the site's hours, its standing breaks, a schedule that never covered the
    // row, somebody's absence. For a row that is already booked the appointment exists, and the only
    // thing that makes it a problem is somebody having since said they are not there — which is a date
    // override, and nothing else. The site closing at noon, the office's lunch, and a host who was
    // never on the rotation are all reasons Cal will not sell an hour; none of them is news about an
    // appointment already taken, and marking them put a conflict flag on ordinary bookings.
    //
    // It also means the flag no longer depends on the event type's availability at all, which is what
    // made it fire on a host booked solid: an hour sold out entirely is missing from `dateRanges`
    // exactly as an hour off is, and no amount of reading those ranges can tell the two apart.
    if (cell.state !== "open" && !override) return
    cells[index] = {
      ...cell,
      // A booked row keeps its state: the appointment is still real, and still has to be dealt with.
      state: cell.state === "open" ? "unavailable" : cell.state,
      unavailable: {
        label: standing?.label ?? unavailableLabel(override, capped),
        reason: override?.reason,
        scope: override?.scope,
        partial: partly || undefined,
        scheduledBreak: standing ? true : undefined,
        dailyLimit: capped && !override ? true : undefined,
      },
    }
  })
}

/** Cuts an earlier appointment's span short at `row`, which `by` has claimed. */
function truncateSpanBefore(cells: SlotData[], row: number, by: BookingDetails): void {
  const uid = cells[row]!.slotId

  for (let candidate = row - 1; candidate >= 0; candidate--) {
    const cell = cells[candidate]!
    if (cell.slotId !== uid) break
    if (!cell.continuation) {
      cell.spanRows = row - candidate
      recordOverlap(cell, by)
      break
    }
  }

  // The rows below the split belong to the same appointment's tail, and its head now stops above
  // them. Left as they are they would be continuation cells with nothing above to continue — drawn,
  // on screen, as part of whichever appointment took the split row. Returned to open, which is what
  // they are: the row the truncated appointment no longer reaches.
  for (let below = row + 1; below < cells.length; below++) {
    const cell = cells[below]!
    if (cell.slotId !== uid || !cell.continuation) break
    cells[below] = { time: cell.time, startMinutes: cell.startMinutes, state: "open" }
  }
}

/**
 * Builds the visible day: business hours give it structure, the bookings fill it in, and each
 * host's availability takes back the rows nobody is working.
 *
 * The order is deliberate. Bookings are laid down first, so that a row which is both booked and
 * unworked comes out as a flagged appointment rather than as an empty unavailable slot - the
 * appointment is the thing that still needs doing something about.
 */
export function buildDayGrid(input: BuildDayGridInput): DaySchedule {
  const { date, bookings, columns, timeZone, availability } = input
  const businessHours = input.businessHours ?? DEFAULT_BUSINESS_HOURS

  // A team whose event type reports a nonsensical length would otherwise divide by zero and hang
  // the row loop.
  const slotMinutes = input.slotMinutes > 0 ? input.slotMinutes : 30

  const placed = placeBookings(bookings, date, slotMinutes, timeZone)
  const rows = rowAxis(slotMinutes, businessHours)
  if (rows.length === 0) return {}

  const base = baseDay(rows)
  const schedule: DaySchedule = {}
  const slotTypes = slotTypeEventTypes(input.privateEventTypeIds, input.offSlotEventTypeIds)

  // Which rows the public could not book, worked out once for the day: every column shares the same
  // rows, and a row's start is the same instant whoever's column it is in.
  const outsideSeason = rows.map(rowStart =>
    isOutsideBookingSeason(zonedTimeToInstant(date, rowStart, timeZone), input.bookingSeason))

  for (const column of columns) {
    const cells = base.map(cell => ({ ...cell }))
    const mine = placed.filter(p => p.booking.hosts.some(host => host.id === column.calUserId))

    for (const booking of mine) overlay(cells, rows, booking, slotMinutes, businessHours, slotTypes)

    // Only when availability was actually read. Without it the grid says nothing about who is
    // working, exactly as it did before it could ask - rather than shading every row of every
    // column because it found no ranges.
    if (availability) {
      const forColumn = availability.find(host => host.userId === column.calUserId)
      markUnavailable(
        cells,
        rows,
        placeAvailability(forColumn, date, timeZone),
        forColumn,
        date,
        slotMinutes,
        slotTypes)
    }

    // Last, and only ever added: it says nothing about whether staff can book the row, so it must not
    // touch `state`. After everything else because a split span hands its tail rows back as fresh
    // cells, which would drop a flag laid down any earlier.
    cells.forEach((cell, index) => {
      if (outsideSeason[index]) cells[index] = { ...cell, outsideBookingSeason: true }
    })

    schedule[column.id] = cells
  }

  return schedule
}

/** One unbroken run of dead rows with a single cause, which is what the column label lists. */
interface UnavailableStretch {
  label: string
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
}

/**
 * The dead rows gathered into stretches: adjacent, and blamed on the same thing.
 *
 * Blame matters: a run that starts as a site closure and ends as somebody's afternoon out is two
 * things a scheduler reads differently. Adjacency is by bookable time rather than by clock, so a
 * standing break between two dead rows does not split them — an absence from 11:30 until close is
 * one absence, and reporting it as two either side of lunch invites somebody to try to book into an
 * hour that was never on offer.
 */
function unavailableStretches(
  unworked: { rowStart: MinuteOfDay; override: AvailabilityOverride | undefined }[],
  slotMinutes: number,
  dailyLimit = false,
): UnavailableStretch[] {
  const stretches: UnavailableStretch[] = []

  for (const { rowStart, override } of unworked) {
    const label = unavailableLabel(override, dailyLimit)
    const open = stretches.at(-1)

    if (open && open.label === label && onlyBreakBetween(open.endMinutes, rowStart)) {
      open.endMinutes = rowStart + slotMinutes
      continue
    }

    stretches.push({ label, startMinutes: rowStart, endMinutes: rowStart + slotMinutes })
  }

  return stretches
}

/**
 * Each column's standing for the whole day, for the places that label a person rather than a slot -
 * the roster in the sidebar, the column header.
 *
 * Columns bookable through their whole working day are absent from the map rather than present and
 * empty, so a caller can test for the key — and the office's standing breaks do not put one in it.
 * Somebody in all day is in all day; saying "unavailable at lunch" beside their name would read as
 * an absence, and worse, would drown the columns with a real one in a header that names everybody.
 */
export function unavailabilityByColumn(input: BuildDayGridInput): Record<string, UnavailabilityMark> {
  const businessHours = input.businessHours ?? DEFAULT_BUSINESS_HOURS
  const slotMinutes = input.slotMinutes > 0 ? input.slotMinutes : 30

  const rows = rowAxis(slotMinutes, businessHours)
  if (rows.length === 0 || !input.availability) return {}

  const marks: Record<string, UnavailabilityMark> = {}

  // The office's own break and lunch are not news about a person: everybody has them, every day.
  // They are left out of the account entirely — even when an absence covers them too, since a
  // stretch reported as ending at lunch and resuming after it says nothing a scheduler can use.
  const workRows = rows.filter(rowStart => !breakAt(rowStart, slotMinutes))

  const placed = placeBookings(input.bookings, input.date, slotMinutes, input.timeZone)

  for (const column of input.columns) {
    const forColumn = input.availability.find(host => host.userId === column.calUserId)
    const windows = placeAvailability(forColumn, input.date, input.timeZone)
    const mine = placed.filter(p => p.booking.hosts.some(host => host.id === column.calUserId))

    const unworked = workRows
      .filter(rowStart =>
        !windows.some(w => w.startMinutes <= rowStart && w.endMinutes >= rowStart + slotMinutes))
      .map(rowStart => {
        const span = { startMinutes: rowStart, endMinutes: rowStart + slotMinutes }
        return { rowStart, span, override: explainingOverride(forColumn, input.date, span) }
      })
      // Their own appointments, which Cal withheld because it had already sold them. A person booked
      // solid is the opposite of a person who is away, and reporting the hours they are busiest as
      // "Unavailable" beside their name says exactly the wrong thing about the day. An override still
      // counts, because somebody stating they are not there is not made untrue by an appointment
      // already on the books — that is the case this is here to report.
      .filter(({ span, override }) => !!override || !bookedOver(mine, span))

    if (unworked.length === 0) continue

    // The day's reason, for the callers that want one rather than the whole account of it. A site
    // closure again wins over a personal absence.
    const blamed = unworked.map(u => u.override).filter(o => !!o)
    const override = blamed.find(o => o.scope === "event-type") ?? blamed[0]

    const capped = isDailyLimitDay(forColumn, input.date)
    const stretches = unavailableStretches(unworked, slotMinutes, capped)
    const allDay = unworked.length === workRows.length

    marks[column.id] = {
      // "All day" is worth saying plainly; anything less gets the hours, because when somebody is
      // back is the thing a scheduler is actually trying to find out. Every stretch is named, not
      // just the first and last: running them together would report the hours between two absences
      // as unavailable when they are exactly the hours somebody is trying to book into.
      label: allDay && stretches.length === 1
        ? unavailableLabel(override, capped)
        : stretches
          .map(s => `${s.label} · ${minutesToLabel(s.startMinutes)}–${minutesToLabel(s.endMinutes)}`)
          .join(", "),
      reason: override?.reason,
      scope: override?.scope,
      partial: !allDay || undefined,
      dailyLimit: capped && !override ? true : undefined,
    }
  }

  return marks
}

/** Whether any column hosts this appointment — which is whether the grid has anywhere to draw it. */
function hasColumn(booking: BookingDetails, columns: GridColumn[]): boolean {
  return columns.some(column => booking.hosts.some(host => host.id === column.calUserId))
}

/**
 * The day's appointments that the grid has no row for: over before the office opened, or starting
 * after the last row begins. The grid is now exactly business hours, so these cannot be drawn —
 * but an appointment nobody can see is worse than an awkward one, so the page counts them out loud
 * rather than letting them disappear.
 *
 * Only ones a column hosts. An appointment hosted by nobody on the grid has no row for a different
 * reason and is counted by `bookingsWithoutColumn` — reporting it here would tell a manager to look
 * for an out-of-hours booking that does not exist.
 */
export function bookingsOutsideWindow(input: BuildDayGridInput): BookingDetails[] {
  const { date, bookings, columns, timeZone } = input
  const businessHours = input.businessHours ?? DEFAULT_BUSINESS_HOURS
  const slotMinutes = input.slotMinutes > 0 ? input.slotMinutes : 30

  const rows = rowAxis(slotMinutes, businessHours)
  if (rows.length === 0) return []

  const axisStart = rows[0]!
  const axisEnd = rows.at(-1)! + slotMinutes

  return placeBookings(bookings, date, slotMinutes, timeZone)
    .filter(placed => hasColumn(placed.booking, columns))
    .filter(placed => placed.endMinutes <= axisStart || placed.startMinutes >= axisEnd)
    .map(placed => placed.booking)
}

/**
 * The day's appointments hosted by nobody the grid has a column for.
 *
 * What deactivation leaves behind: taking somebody off a team removes them from the roster the
 * columns are built from, while the appointments they were already hosting stay in Cal. They are
 * still the site's appointments and somebody still has to cover them, so they are counted rather
 * than left as a number in the toolbar that no column on screen accounts for.
 */
export function bookingsWithoutColumn(input: BuildDayGridInput): BookingDetails[] {
  const { date, bookings, columns, timeZone } = input
  const slotMinutes = input.slotMinutes > 0 ? input.slotMinutes : 30

  return placeBookings(bookings, date, slotMinutes, timeZone)
    .filter(placed => !hasColumn(placed.booking, columns))
    .map(placed => placed.booking)
}
